import { gameHttpClient } from '@/services/httpClient';
import type {
  DemoResponse,
  EndBoardResponse,
  EndGameResponse,
  GameApiEnvelope,
  GameBoard,
  GameMove,
  GameSession,
  GameStatusResponse,
  GenerateShufflesResponse,
  SubmitMovesResponse,
} from '@/types/sliding-puzzle';

/**
 * All game API calls matching the server contract exactly.
 * Every endpoint that is scoped to a stage requires stageId.
 * Responses keep the server envelope so callers can inspect success/status/message.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toEnvelope<T>(response: { data: GameApiEnvelope<T> | T }): GameApiEnvelope<T> {
  const payload = response.data;

  if (
    isRecord(payload) &&
    ('success' in payload || 'statusCode' in payload || 'message' in payload || 'data' in payload)
  ) {
    return {
      success: typeof payload.success === 'boolean' ? payload.success : true,
      statusCode: typeof payload.statusCode === 'number' ? payload.statusCode : undefined,
      message: typeof payload.message === 'string' ? payload.message : undefined,
      data: payload.data as T | undefined,
    };
  }

  return { success: true, data: payload as T };
}

export class GameApiEnvelopeError<T = unknown> extends Error {
  readonly envelope: GameApiEnvelope<T>;

  constructor(response: GameApiEnvelope<T>, fallbackMessage = 'Game API request failed') {
    super(response.message ?? fallbackMessage);
    this.name = 'GameApiEnvelopeError';
    this.envelope = response;
  }
}

export function ensureGameApiSuccess<T>(response: GameApiEnvelope<T>): GameApiEnvelope<T> {
  if (!response.success) {
    throw new GameApiEnvelopeError(response);
  }
  return response;
}

export function requireGameApiData<T>(
  response: GameApiEnvelope<T>,
  fallbackMessage = 'Game API response missing data',
): T {
  ensureGameApiSuccess(response);

  if (response.data == null) {
    throw new Error(response.message ?? fallbackMessage);
  }

  return response.data;
}

/** GET /v1/user/demo?stageId=... */
export async function getDemo(stageId: string): Promise<GameApiEnvelope<DemoResponse>> {
  const res = await gameHttpClient.get<GameApiEnvelope<DemoResponse>>('/v1/user/demo', {
    params: { stageId },
  });
  return toEnvelope(res);
}

export interface GameStartParams {
  stageId: string;
  gameSessionId: string;
  key: string;
}

/** POST /v1/game/game-start */
export async function gameStart(params: GameStartParams): Promise<GameApiEnvelope<GameSession>> {
  const res = await gameHttpClient.post<GameApiEnvelope<GameSession>>('/v1/game/game-start', {
    stageId: params.stageId,
    gameSessionId: params.gameSessionId,
    key: params.key,
  });
  return toEnvelope(res);
}

/** POST /v1/game/submit-moves */
export async function submitMoves(
  stageId: string,
  moves: GameMove[],
): Promise<GameApiEnvelope<SubmitMovesResponse>> {
  const res = await gameHttpClient.post<GameApiEnvelope<SubmitMovesResponse>>(
    '/v1/game/submit-moves',
    { stageId, moves },
  );
  return toEnvelope(res);
}

/** POST /v1/game/end-board */
export async function endBoard(skip?: boolean): Promise<GameApiEnvelope<EndBoardResponse>> {
  const urlStageId = new URLSearchParams(window.location.search).get('stageId') ?? '';
  const body: Record<string, unknown> = {};
  if (urlStageId) body.stageId = urlStageId;
  if (skip) body.skip = true;
  const res = await gameHttpClient.post<GameApiEnvelope<EndBoardResponse>>(
    '/v1/game/end-board',
    Object.keys(body).length > 0 ? body : undefined,
  );
  return toEnvelope(res);
}

/** POST /v1/game/end-game (forfeit) */
export async function endGame(stageId: string): Promise<GameApiEnvelope<EndGameResponse>> {
  const res = await gameHttpClient.post<GameApiEnvelope<EndGameResponse>>('/v1/game/end-game', {
    stageId,
  });
  return toEnvelope(res);
}

/** GET /v1/game/next-board?stageId=... */
export async function getNextBoard(stageId: string): Promise<GameApiEnvelope<GameBoard>> {
  const res = await gameHttpClient.get<GameApiEnvelope<GameBoard>>('/v1/game/next-board', {
    params: { stageId },
  });
  return toEnvelope(res);
}

/** GET /v1/game/status?stageId=... */
export async function getGameStatus(stageId: string): Promise<GameApiEnvelope<GameStatusResponse>> {
  const res = await gameHttpClient.get<GameApiEnvelope<GameStatusResponse>>('/v1/game/status', {
    params: { stageId },
  });
  return toEnvelope(res);
}

/** GET /v1/board-tools/generate-shuffles */
export async function generateShuffles(
  gridX: number,
  gridY: number,
  count: number,
): Promise<GameApiEnvelope<GenerateShufflesResponse>> {
  const res = await gameHttpClient.get<GameApiEnvelope<GenerateShufflesResponse>>(
    '/v1/board-tools/generate-shuffles',
    {
      params: { gridX, gridY, count },
    },
  );
  return toEnvelope(res);
}
